import {Component, HostListener} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {
    faAngleLeft,
    faAngleRight,
    faArrowCircleLeft,
    faCheck,
    faCopy,
    faFileExcel,
    faPen,
    faSquarePlus
} from '@fortawesome/free-solid-svg-icons';
import {ApiService} from '../../services/api.service';
import {ToastrService} from 'ngx-toastr';
import {getFileNameFromResponse} from '../../../common/utils';
import saveAs from 'file-saver';
import {GenericSearchComponent} from '../../shared/search-suggestion/search-suggestion.component';
import {AuthService} from '../../services/auth.service';
import {CopyButtonComponent} from '../../shared/copy-button/copy-button.component';
import {DEFAULT_TABLE_PAGE_SIZE_OPTIONS} from '../../shared/constants/pagination.constants';
import {SharedTableToolbarComponent} from '../../shared/table-toolbar/shared-table-toolbar.component';
import {VtePaginationComponent} from '../../shared/vte/vte-pagination/vte-pagination.component';

@Component({
    selector: 'app-view-bridges',
    imports: [CommonModule, FormsModule, FontAwesomeModule, GenericSearchComponent, CopyButtonComponent, SharedTableToolbarComponent, VtePaginationComponent],
    templateUrl: './view-bridges.component.html',
    styleUrl: './view-bridges.component.css'
})
export class ViewBridgesComponent {
    readonly pageSizeOptions = DEFAULT_TABLE_PAGE_SIZE_OPTIONS;
    searchText: string | null = '';
    recordsPerPage = 10;
    currentPage = 1;
    bridgeData: any[] = [];
    nextPage: number | null = null;
    previousPage: number | null = null;
    totalRecords = 0;
    showStatusConfirmationDialog = false;
    pendingStatusChange: { record: any; nextStatus: boolean } | null = null;
    orgId!: number;
    orgName!: string;
    isSuperUser = false;
    searchExtraBody: Record<string, string | number> = {};

    constructor(
        private apiService: ApiService,
        library: FaIconLibrary,
        private router: Router,
        private toastr: ToastrService,
        private authService: AuthService,
        private route: ActivatedRoute
    ) {
        library.addIcons(
            faFileExcel,
            faAngleRight,
            faAngleLeft,
            faSquarePlus,
            faPen,
            faArrowCircleLeft,
            faCopy,
            faCheck
        );

        this.route.queryParams.subscribe(params => {
            this.orgId = Number(params['id']);
            this.orgName = params['name'];
            this.searchExtraBody = {org_id: this.orgId};
            this.fetchData(this.currentPage, this.recordsPerPage);
        });

        this.isSuperUser = this.authService.isSuperUser();
    }

    fetchData(page: number, pageSize: number) {
        this.apiService.viewBridges(page, pageSize, this.searchText, this.orgId).subscribe({
            next: (data: any) => {
                this.bridgeData = data['results'];
                this.totalRecords = data['count'];
                this.nextPage = data['next'];
                this.previousPage = data['previous'];
            }
        });
    }

    get totalPages(): number {
        let totalPage = Math.ceil(this.totalRecords / this.recordsPerPage);
        if (totalPage === 0) {
            totalPage = 1;
        }
        return totalPage;
    }

    next() {
        if (this.nextPage) {
            this.currentPage++;
            this.fetchData(this.currentPage, this.recordsPerPage);
        }
    }

    previous() {
        if (this.previousPage) {
            this.currentPage--;
            this.fetchData(this.currentPage, this.recordsPerPage);
        }
    }

    selectPageSize() {
        if (this.recordsPerPage) {
            this.currentPage = 1;
            this.fetchData(this.currentPage, this.recordsPerPage);
        }
    }

    onPageChange(page: number): void {
        this.currentPage = page;
        this.fetchData(this.currentPage, this.recordsPerPage);
    }

    onPageSizeChange(pageSize: number): void {
        this.recordsPerPage = pageSize;
        this.currentPage = 1;
        this.fetchData(this.currentPage, this.recordsPerPage);
    }

    exportToExcel() {
        this.apiService.exportBridges(this.currentPage, this.recordsPerPage, this.searchText, this.orgId).subscribe({
            next: (response: any) => {
                const fileName = getFileNameFromResponse(response);
                if (response.body) {
                    saveAs(response.body, fileName);
                }
            },
            error: (err) => {
                this.toastr.error(`${err}`, 'Error');
            }
        });
    }

    backPage() {
        this.router.navigate(['organization'], {state: {activeTab: 'customer'}});
    }

    onToggleChange(record: any, event: Event) {
        const target = event.target as HTMLInputElement;
        const nextStatus = target.checked;
        target.checked = record.is_active;
        this.pendingStatusChange = {record, nextStatus};
        this.showStatusConfirmationDialog = true;
    }

    closeStatusConfirmationDialog() {
        this.showStatusConfirmationDialog = false;
        this.pendingStatusChange = null;
    }

    get statusDialogTitle(): string {
        return this.pendingStatusChange?.nextStatus ? 'Enable Bridge' : 'Disable Bridge';
    }

    get statusDialogMessage(): string {
        if (!this.pendingStatusChange) {
            return '';
        }
        if (this.pendingStatusChange.nextStatus) {
            return 'Are you sure you want to enable this bridge?';
        }
        return (
            'Are you sure you want to disable this bridge? ' +
            'The bridge will no longer synchronize until it is enabled again.'
        );
    }

    confirmStatusChange() {
        if (!this.pendingStatusChange) {
            return;
        }

        const {record, nextStatus} = this.pendingStatusChange;
        record._toggling = true;
        this.apiService.updateBridge(record.id, this.orgId, {is_active: nextStatus}).subscribe({
            next: () => {
                record.is_active = nextStatus;
                record._toggling = false;
                this.toastr.success(
                    nextStatus ? 'Bridge enabled successfully.' : 'Bridge disabled successfully.',
                    'Success'
                );
                this.closeStatusConfirmationDialog();
            },
            error: (err) => {
                record._toggling = false;
                if (err['error']?.detail) {
                    this.toastr.error(err['error']['detail'], 'Error');
                } else if (err['status'] !== 403 && err.status !== 0) {
                    this.toastr.error('Something went wrong!', 'Error');
                }
                this.closeStatusConfirmationDialog();
            }
        });
    }

    updateBridge(bridgeId: string) {
        return this.router.navigate([`edit-bridge/${bridgeId}`], {
            queryParams: {org_id: this.orgId, name: this.orgName}
        });
    }

    formatRelativeTime(value: string | null | undefined): string {
        if (!value) {
            return 'Never synced';
        }
        const date = new Date(value);
        const diffMs = Date.now() - date.getTime();
        const diffMinutes = Math.floor(diffMs / 60000);
        if (diffMinutes < 1) {
            return 'Just now';
        }
        if (diffMinutes < 60) {
            return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
        }
        const diffHours = Math.floor(diffMinutes / 60);
        if (diffHours < 24) {
            return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
        }
        const diffDays = Math.floor(diffHours / 24);
        return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    }

    addBridge() {
        this.router.navigate(['/add-bridge'], {
            queryParams: {id: this.orgId, name: this.orgName}
        });
    }

    onResults(event: {
        searchText: string;
        results: any[];
        totalRecords: number;
        nextPage: number | null;
        previousPage: number | null;
    }) {
        this.currentPage = 1;
        this.recordsPerPage = 10;
        this.previousPage = event.previousPage;
        this.nextPage = event.nextPage;
        this.searchText = event.searchText;
        this.bridgeData = event.results;
        this.totalRecords = event.totalRecords;
    }

    @HostListener('document:click', ['$event'])
    clickOutside(event: Event) {
        const target = event.target as HTMLElement;
        const modalElement = document.querySelector('.modal-content');
        if (modalElement && !modalElement.contains(target)) {
            // reserved for future detail modal
        }
    }
}
